import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { progressAPI } from '../utils/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { BookOpen, TrendingUp, Calendar, Award, Sparkles, AlertCircle, Target } from 'lucide-react';
import './Dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [missedDays, setMissedDays] = useState([]);
  const [showMissedDaysWarning, setShowMissedDaysWarning] = useState(false);

  const checkMissedDays = useCallback(async (statusData) => {
    try {
      const progressResponse = await progressAPI.getAllProgress();
      const allProgress = progressResponse.data.progress;
      
      const missed = [];
      for (let day = 1; day < statusData.currentDay; day++) {
        if (!allProgress[day] || !allProgress[day].completedAt) {
          missed.push(day);
        }
      }
      
      setMissedDays(missed);
      setShowMissedDaysWarning(missed.length > 0);
    } catch (err) {
      console.error('Error checking missed days:', err);
    }
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await progressAPI.getStatus();
      setStatus(response.data);
    } catch (err) {
      setError('Failed to load progress status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  useEffect(() => {
    if (status && status.started) {
      checkMissedDays(status);
    }
  }, [status, checkMissedDays]);

  const handleStartJourney = async () => {
    try {
      await progressAPI.start();
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start journey');
    }
  };

  const motivationMessages = [
    { en: "Great job staying consistent!", te: "మంచి పని! స్థిరంగా కొనసాగుతున్నారు!" },
    { en: "You're growing in the Word of God!", te: "మీరు దేవుని వాక్యంలో ఎదుగుతున్నారు!" },
    { en: "Don't give up — God's Word strengthens you!", te: "వదులుకోవద్దు — దేవుని వాక్యం మిమ్మల్ని బలపరుస్తుంది!" },
  ];

  const randomMessage = motivationMessages[Math.floor(Math.random() * motivationMessages.length)];

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container">
          <div className="loading-spinner">Loading...</div>
        </div>
      </>
    );
  }

  const dashboardStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "HolyBible365 Dashboard",
  "description": "Track your Bible reading progress, view statistics, and continue your spiritual journey with HolyBible365.",
  "url": "https://holybible365.com/dashboard",
  "isPartOf": {
    "@type": "WebSite",
    "name": "HolyBible365",
    "url": "https://holybible365.com"
  },
  "mainEntity": {
    "@type": "WebApplication",
    "name": "HolyBible365",
    "applicationCategory": "EducationalApplication"
  }
};

return (
    <>
      <SEO 
        title="Dashboard - Track Your Bible Reading Progress"
        description="Monitor your daily Bible reading progress, view completion statistics, and stay motivated on your spiritual journey with HolyBible365."
        keywords="Bible reading progress, spiritual growth dashboard, Christian devotion tracker, Bible study statistics"
        canonicalUrl="https://holybible365.com/dashboard"
        structuredData={dashboardStructuredData}
      />
      <Navbar />
      <div className="dashboard-container">
        <div className="container">
          <div className="welcome-section card">
            <div className="welcome-header">
              <h1>Welcome, {status?.firstName || user?.firstName}! 🙏</h1>
              <p className="welcome-subtitle">
                {user?.languagePreference === 'telugu' || user?.languagePreference === 'both' 
                  ? 'స్వాగతం!' 
                  : 'May God bless your reading journey'}
              </p>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          {/* Missed Days Warning */}
          {showMissedDaysWarning && missedDays.length > 0 && (
            <div className="dashboard-missed-days-warning card">
              <div className="dashboard-warning-header">
                <AlertCircle size={32} className="dashboard-warning-icon" />
                <div>
                  <h3>⚠️ Missed Days Detected</h3>
                  <p>You have {missedDays.length} missed day{missedDays.length > 1 ? 's' : ''}</p>
                </div>
              </div>
              
              <div className="dashboard-missed-days-summary">
                <div className="missed-days-badges">
                  {missedDays.slice(0, 5).map(day => (
                    <span key={day} className="dashboard-missed-day-badge">Day {day}</span>
                  ))}
                  {missedDays.length > 5 && (
                    <span className="dashboard-missed-day-more">+{missedDays.length - 5} more</span>
                  )}
                </div>
              </div>

              <div className="dashboard-warning-actions">
                <button 
                  onClick={() => navigate('/plan')} 
                  className="btn btn-warning btn-medium"
                >
                  <Target size={16} />
                  Catch Up Now
                </button>
                <button 
                  onClick={() => setShowMissedDaysWarning(false)} 
                  className="btn btn-outline btn-medium"
                >
                  Remind Later
                </button>
              </div>
            </div>
          )}

          {!status?.started ? (
            <div className="start-journey-section card">
              <div className="journey-hero">
                <BookOpen size={64} className="hero-icon" />
                <h2>Begin Your 365-Day Bible Reading Journey</h2>
                <p>
                  Embark on a transformative spiritual journey through God's Word. 
                  Read the entire Bible in one year with our structured daily plan.
                </p>
                <button onClick={handleStartJourney} className="btn btn-primary btn-large">
                  <Sparkles size={20} />
                  Start Bible Reading 365
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="stats-grid">
                <div className="stat-card card">
                  <div className="stat-icon">
                    <Calendar size={32} />
                  </div>
                  <div className="stat-content">
                    <h3>Day {status.currentDay}</h3>
                    <p>of 365</p>
                  </div>
                </div>

                <div className="stat-card card">
                  <div className="stat-icon success">
                    <Award size={32} />
                  </div>
                  <div className="stat-content">
                    <h3>{status.completedDays}</h3>
                    <p>Days Completed</p>
                  </div>
                </div>

                <div className="stat-card card">
                  <div className="stat-icon progress">
                    <TrendingUp size={32} />
                  </div>
                  <div className="stat-content">
                    <h3>{status.progressPercentage}%</h3>
                    <p>Progress</p>
                  </div>
                </div>
              </div>

              <div className="progress-section card">
                <h3>Your Progress</h3>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${status.progressPercentage}%` }}
                  >
                    {status.progressPercentage}%
                  </div>
                </div>
                <p className="progress-text">
                  {status.completedDays} of 365 days completed
                </p>
              </div>

              <div className="motivation-section card">
                <Sparkles className="motivation-icon" size={32} />
                <h3>Daily Encouragement</h3>
                <p className="motivation-text">{randomMessage.en}</p>
                {(user?.languagePreference === 'telugu' || user?.languagePreference === 'both') && (
                  <p className="motivation-text telugu">{randomMessage.te}</p>
                )}
              </div>

              <div className="action-section card">
                <h3>
                  {status.todayCompleted 
                    ? "✅ Today's Reading Completed!" 
                    : `📖 Day ${status.currentDay} Reading`}
                </h3>
                {!status.todayCompleted && (
                  <p>Continue your journey with today's Bible reading</p>
                )}
                <button 
                  onClick={() => navigate('/today')} 
                  className={`btn ${status.todayCompleted ? 'btn-secondary' : 'btn-primary'} btn-large`}
                >
                  <BookOpen size={20} />
                  {status.todayCompleted ? 'Review Today\'s Reading' : 'Start Today\'s Reading'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Dashboard;
